/**
 * ContactSection.jsx
 * Dynamic Clinic Details Version
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUserAuth } from "../../../context/UserAuthContext";
import { apiFetch } from "../../helpers";

const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  subject: "",
  message: "",
};

const SUBJECTS = [
  "General Inquiry",
  "Appointment Query",
  "Billing & Insurance",
  "Emergency",
  "Feedback",
  "Other",
];

export default function ContactSection() {
  const isMobile = window.innerWidth <= 768;
  const navigate = useNavigate();
  const { isLoggedIn, user } = useUserAuth();

  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSub] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const [clinicInfo, setClinicInfo] = useState({
    clinic_name: "",
    email: "",
    phone: "",
    full_address: "",
    mon_fri_open: "09:00",
    mon_fri_close: "20:00",
    saturday_open: "09:00",
    saturday_close: "16:00",
    sunday_closed: 1,
  });

  useEffect(() => {
    loadClinic();
  }, []);

  const loadClinic = async () => {
    try {
      const json = await apiFetch(`${process.env.REACT_APP_GET_CLINIC}`);

      if (json.status && json.data) {
        setClinicInfo(json.data);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.message.trim()) {
      return setError("Name and message are required.");
    }

    setSub(true);
    setError("");

    try {
      const json = await apiFetch(`${process.env.REACT_APP_CONTACT_US}`, {
        method: "POST",
        body: {
          ...form,
          user_id: user?.user_id || null,
        },
      });

      if (json.status) {
        setSuccess(true);
        setForm(EMPTY_FORM);
      } else {
        setError(json.message || "Failed");
      }
    } catch {
      setError("Network Error");
    }

    setSub(false);
  };

  const clinicRows = [
    {
      icon: "📍",
      label: "Address",
      value: clinicInfo.full_address,
    },
    {
      icon: "📞",
      label: "Phone",
      value: clinicInfo.phone,
      link: `tel:${clinicInfo.phone}`,
    },
    {
      icon: "📧",
      label: "Email",
      value: clinicInfo.email,
      link: `mailto:${clinicInfo.email}`,
    },
    {
      icon: "🕐",
      label: "Hours",
      value: `Mon–Fri: ${clinicInfo.mon_fri_open} - ${clinicInfo.mon_fri_close}
Sat: ${clinicInfo.saturday_open} - ${clinicInfo.saturday_close}
Sun: ${clinicInfo.sunday_closed == 1 ? "Closed" : "Open"}`,
    },
  ];

  return (
    <section id="contact"
      style={{
        background: "#0A1628",
        padding: "60px 20px",
        color: "#fff",
      }}
    >
      <div
        style={{
          padding: 45,
        }}
      ></div>
      <div
        style={{
          maxWidth: 1200,
          margin: "0 auto",
        }}
      >
        <div
          style={{ textAlign: "center", marginBottom: 40}}
        >
          <h2
            style={{
              fontSize: isMobile ? 28 : 36,
              fontWeight: 800,
            }}
          >
            Contact Us
          </h2>
          <p style={{ color: "#ccc" }}>We’d love to hear from you</p>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1.2fr",
            gap: 30,
          }}
        >
          {/* LEFT */}
          <div>
            <div
              style={{
                background: "#13233d",
                padding: 25,
                borderRadius: 16,
              }}
            >
              <h3 style={{ marginBottom: 20 }}>Clinic Information</h3>

              {clinicRows.map((row) => (
                <div
                  key={row.label}
                  style={{
                    marginBottom: 20,
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      color: "#aaa",
                      marginBottom: 5,
                    }}
                  >
                    {row.icon} {row.label}
                  </div>

                  {row.link ? (
                    <a
                      href={row.link}
                      style={{
                        color: "#1D9E75",
                        textDecoration: "none",
                      }}
                    >
                      {row.value}
                    </a>
                  ) : (
                    <div
                      style={{
                        whiteSpace: "pre-line",
                      }}
                    >
                      {row.value}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div
              style={{
                marginTop: 20,
                display: "grid",
                gap: 10,
              }}
            >
              <a href={`tel:${clinicInfo.phone}`} style={btnStyle}>
                📞 Call Us
              </a>

              <a
                href={`https://wa.me/${clinicInfo.phone.replace(/\D/g, "")}`}
                target="_blank"
                rel="noreferrer"
                style={btnStyle}
              >
                💬 WhatsApp
              </a>

              <a
                href={`https://maps.google.com?q=${encodeURIComponent(
                  clinicInfo.full_address,
                )}`}
                target="_blank"
                rel="noreferrer"
                style={btnStyle}
              >
                🗺️ Directions
              </a>
            </div>
          </div>

          {/* RIGHT */}
          <div
            style={{
              background: "#13233d",
              padding: 25,
              borderRadius: 16,
            }}
          >
            {!isLoggedIn ? (
              <div style={{ textAlign: "center" }}>
                <h3>🔐 Login Required</h3>
                <p>Please login to send message.</p>

                <button onClick={() => navigate("/login")} style={btnStyle}>
                  Login
                </button>
              </div>
            ) : success ? (
              <div style={{ textAlign: "center" }}>
                <h3>🎉 Message Sent!</h3>
                <button onClick={() => setSuccess(false)} style={btnStyle}>
                  Send Another
                </button>
              </div>
            ) : (
              <>
                <h3 style={{ marginBottom: 20 }}>Send Message</h3>

                <input
                  placeholder="Full Name"
                  value={form.name}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      name: e.target.value,
                    })
                  }
                  style={inputStyle}
                />

                <input
                  placeholder="Phone"
                  value={form.phone}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      phone: e.target.value,
                    })
                  }
                  style={inputStyle}
                />

                <input
                  placeholder="Email"
                  value={form.email}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      email: e.target.value,
                    })
                  }
                  style={inputStyle}
                />

                <select
                  value={form.subject}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      subject: e.target.value,
                    })
                  }
                  style={inputStyle}
                >
                  <option value="">Select Subject</option>

                  {SUBJECTS.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>

                <textarea
                  rows="5"
                  placeholder="Message"
                  value={form.message}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      message: e.target.value,
                    })
                  }
                  style={inputStyle}
                />

                {error && (
                  <div
                    style={{
                      color: "salmon",
                      marginBottom: 10,
                    }}
                  >
                    {error}
                  </div>
                )}

                <button
                  onClick={handleSubmit}
                  style={btnStyle}
                  disabled={submitting}
                >
                  {submitting ? "Sending..." : "Send Message"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

const btnStyle = {
  padding: "12px 18px",
  background: "#1D9E75",
  color: "#fff",
  border: "none",
  borderRadius: 8,
  cursor: "pointer",
  textDecoration: "none",
  display: "inline-block",
  width: "100%",
  textAlign: "center",
};

const inputStyle = {
  width: "100%",
  padding: 12,
  marginBottom: 12,
  borderRadius: 8,
  border: "1px solid #334",
  background: "#0f1c31",
  color: "#fff",
};
